import { useEffect, useState } from 'react';
import { getSupabase } from './supabaseClient';

export type AuthStatus = 'loading' | 'signedOut' | 'signedIn';

export interface AuthState {
  /** Where the session stands: still resolving, needs a login, or signed in. */
  status: AuthStatus;
  /** A genuine Supabase access token once a user is signed in. */
  token: string | null;
  /**
   * A user-facing (Estonian) reason auth could not start — missing build-time
   * Supabase config or a failed dev auto-login. Shown above the login form.
   */
  error: string | null;
  /**
   * True while the session came from a Supabase recovery link — the app shows
   * the new-password screen instead of the shell until the password is saved.
   */
  passwordRecovery: boolean;
  /** Email+password login; resolves to an Estonian error message or null on success. */
  signIn: (email: string, password: string) => Promise<string | null>;
  /** Sends the reset email; resolves to an Estonian error message or null on success. */
  requestPasswordReset: (email: string) => Promise<string | null>;
  /** Saves the new password on the recovery session; Estonian error or null. */
  updatePassword: (password: string) => Promise<string | null>;
  /** Ends the session (the auth listener flips the app back to the login view). */
  signOut: () => Promise<void>;
}

/** Map a Supabase auth error to a clear Estonian message (no raw blank screens). */
export function loginErrorMessage(message: string): string {
  if (/invalid login credentials/i.test(message)) return 'Vale e-post või parool.';
  if (/email not confirmed/i.test(message)) return 'E-posti aadress on kinnitamata.';
  return `Sisselogimine ebaõnnestus: ${message}`;
}

/** Map a Supabase reset-email error to Estonian. */
export function resetRequestErrorMessage(message: string): string {
  if (/rate limit|security purposes/i.test(message)) {
    return 'Liiga palju katseid — oota hetk ja proovi uuesti.';
  }
  return `Taastamiskirja saatmine ebaõnnestus: ${message}`;
}

/** Map a Supabase password-update error to Estonian. */
export function updatePasswordErrorMessage(message: string): string {
  if (/password should be at least/i.test(message)) {
    return 'Parool on liiga lühike (vähemalt 6 märki).';
  }
  if (/should be different from the old password/i.test(message)) {
    return 'Uus parool peab erinema vanast paroolist.';
  }
  return `Parooli salvestamine ebaõnnestus: ${message}`;
}

/** True when the current URL carries a Supabase recovery-link fragment. */
function urlLooksLikeRecovery(): boolean {
  return /type=recovery/.test(window.location.hash);
}

/**
 * The real Auth module (replaces the old src/dev/useDevAuth): production shows
 * the Estonian login form and signs in with Supabase email+password; the
 * session persists across refreshes (persistSession + autoRefreshToken in the
 * shared client). Development keeps the auto-login convenience — when
 * `import.meta.env.DEV` and the dev credentials are present in .env.local, the
 * hook signs the dev user in automatically so RLS still applies exactly as in
 * production (replit.md L7). A failed dev auto-login falls through to the
 * login form with the reason shown, never a blank screen.
 *
 * Password recovery: "Unustasid parooli?" calls resetPasswordForEmail with the
 * current origin as the redirect. Opening the emailed link lands back here
 * with a recovery session — flagged both by the URL fragment (checked before
 * the session resolves, so the app never flashes) and by Supabase's
 * PASSWORD_RECOVERY event — and the app shows the new-password screen until
 * updatePassword succeeds.
 */
export function useAuth(): AuthState {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [passwordRecovery, setPasswordRecovery] = useState<boolean>(() => urlLooksLikeRecovery());

  useEffect(() => {
    let active = true;
    let supabase;
    try {
      supabase = getSupabase();
    } catch {
      // Missing build-time config — surface it, don't blank-screen.
      setError('Autentimine pole seadistatud (Supabase seaded puuduvad sellest buildist).');
      setStatus('signedOut');
      return;
    }

    const { data: subscription } = supabase.auth.onAuthStateChange((event, session) => {
      if (!active) return;
      if (event === 'PASSWORD_RECOVERY') setPasswordRecovery(true);
      const accessToken = session?.access_token ?? null;
      setToken(accessToken);
      setStatus(accessToken ? 'signedIn' : 'signedOut');
    });

    async function resolveSession(): Promise<void> {
      const existing = await getSupabase().auth.getSession();
      if (!active) return;
      const accessToken = existing.data.session?.access_token ?? null;
      if (accessToken) {
        setToken(accessToken);
        setStatus('signedIn');
        return;
      }

      // Dev-only auto-login convenience (the chosen dev flow): credentials come
      // from the gitignored apps/web/.env.local, exposed only when DEV.
      const meta = import.meta.env as unknown as {
        DEV?: boolean;
        VITE_DEV_USER_EMAIL?: string;
        VITE_DEV_USER_PASSWORD?: string;
      };
      if (meta.DEV && meta.VITE_DEV_USER_EMAIL && meta.VITE_DEV_USER_PASSWORD) {
        const signIn = await getSupabase().auth.signInWithPassword({
          email: meta.VITE_DEV_USER_EMAIL,
          password: meta.VITE_DEV_USER_PASSWORD,
        });
        if (!active) return;
        if (signIn.error) {
          setError(`Arenduse automaatne sisselogimine ebaõnnestus: ${signIn.error.message}`);
          setStatus('signedOut');
          return;
        }
        // Success: the onAuthStateChange listener flips status/token.
        return;
      }

      setStatus('signedOut');
    }

    void resolveSession();

    return () => {
      active = false;
      subscription.subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string): Promise<string | null> => {
    const result = await getSupabase().auth.signInWithPassword({ email, password });
    if (result.error) return loginErrorMessage(result.error.message);
    return null;
  };

  const requestPasswordReset = async (email: string): Promise<string | null> => {
    // The SPA serves every path, so the origin itself is a valid landing spot;
    // the recovery tokens arrive in the URL fragment and the client picks them
    // up (detectSessionInUrl). Works on the production URL and in dev alike.
    const result = await getSupabase().auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin,
    });
    if (result.error) return resetRequestErrorMessage(result.error.message);
    return null;
  };

  const updatePassword = async (password: string): Promise<string | null> => {
    const result = await getSupabase().auth.updateUser({ password });
    if (result.error) return updatePasswordErrorMessage(result.error.message);
    // Recovery finished — drop the flag (and any leftover recovery fragment)
    // so the listener's signed-in session now shows the app itself.
    setPasswordRecovery(false);
    if (urlLooksLikeRecovery()) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    return null;
  };

  const signOut = async (): Promise<void> => {
    setPasswordRecovery(false);
    await getSupabase().auth.signOut();
  };

  return {
    status,
    token,
    error,
    passwordRecovery,
    signIn,
    requestPasswordReset,
    updatePassword,
    signOut,
  };
}
